import { supabase } from './supabaseService';
import authService from './authService';

class WebPushService {
  private registration: ServiceWorkerRegistration | null = null;

  // Inicializar service worker
  async init(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ Service Worker no soportado en este navegador');
      return;
    }

    if (!('PushManager' in window)) {
      console.warn('⚠️ Push API no soportado en este navegador');
      return;
    }

    try {
      console.log('📱 Registrando Service Worker...');

      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('✅ Service Worker registrado');

      // NO actualizar suscripción aquí - solo verificar
      // La actualización debe hacerse cuando el usuario esté autenticado
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) {
        console.log('✅ Suscripción push existente encontrada (no se actualiza hasta login)');
      }

    } catch (error) {
      console.error('❌ Error registrando Service Worker:', error);
    }
  }

  // Solicitar permiso y suscribirse a notificaciones
  async subscribe(): Promise<boolean> {
    if (!this.registration) {
      await this.init();
    }

    if (!this.registration) {
      throw new Error('Service Worker no registrado');
    }

    try {
      // Solicitar permiso
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        console.log('⚠️ Permiso de notificaciones denegado');
        return false;
      }

      console.log('✅ Permiso de notificaciones concedido');

      // Verificar si ya hay una suscripción
      let subscription = await this.registration.pushManager.getSubscription();

      if (!subscription) {
        // Crear nueva suscripción
        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(
            import.meta.env.VITE_VAPID_PUBLIC_KEY || this.getDefaultVapidKey()
          )
        });

        console.log('✅ Nueva suscripción creada');
      }

      // Guardar suscripción en la base de datos
      await this.saveSubscription(subscription);

      // Enviar notificación de prueba
      await this.sendTestNotification();

      return true;
    } catch (error) {
      console.error('❌ Error suscribiéndose a notificaciones:', error);
      throw error;
    }
  }

  // Desuscribirse de notificaciones
  async unsubscribe(): Promise<void> {
    if (!this.registration) {
      return;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await this.removeSubscription();
        console.log('✅ Desuscrito de notificaciones push');
      }
    } catch (error) {
      console.error('❌ Error desuscribiéndose:', error);
      throw error;
    }
  }

  // Guardar suscripción en la base de datos
  private async saveSubscription(subscription: PushSubscription): Promise<void> {
    const currentUser = authService.getCurrentSession()?.user;
    if (!currentUser) {
      console.warn('⚠️ Intento de guardar suscripción sin usuario autenticado');
      throw new Error('Usuario no autenticado');
    }

    const subscriptionJSON = subscription.toJSON();
    const pushToken = JSON.stringify(subscriptionJSON);

    // Actualizar preferencias de recordatorios con el token
    const { error } = await supabase
      .from('reminder_preferences')
      .upsert({
        user_id: currentUser.id,
        push_token: pushToken,
        push_enabled: true,
        updated_at: new Date().toISOString()
      });

    if (error) {
      console.error('Error guardando suscripción:', error);
      throw error;
    }

    console.log('✅ Suscripción guardada en base de datos');
  }

  // Actualizar suscripción existente (solo si el usuario está autenticado)
  private async updateSubscription(subscription: PushSubscription): Promise<void> {
    try {
      const currentUser = authService.getCurrentSession()?.user;
      if (!currentUser) {
        console.log('ℹ️ Usuario no autenticado, omitiendo actualización de suscripción');
        return;
      }
      await this.saveSubscription(subscription);
    } catch (error) {
      console.error('Error actualizando suscripción:', error);
    }
  }

  // Eliminar suscripción de la base de datos
  private async removeSubscription(): Promise<void> {
    const currentUser = authService.getCurrentSession()?.user;
    if (!currentUser) return;

    await supabase
      .from('reminder_preferences')
      .update({
        push_token: null,
        push_enabled: false,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', currentUser.id);

    console.log('✅ Suscripción eliminada de base de datos');
  }

  // Enviar notificación de prueba
  private async sendTestNotification(): Promise<void> {
    if (!this.registration) return;

    try {
      await this.registration.showNotification('Notificaciones Activadas', {
        body: 'Recibirás recordatorios de tus reuniones',
        icon: '/logo-ceroideas.png',
        badge: '/logo-ceroideas.png',
        tag: 'test-notification',
        requireInteraction: false,
        vibrate: [200, 100, 200]
      });

      console.log('✅ Notificación de prueba enviada');
    } catch (error) {
      console.error('Error enviando notificación de prueba:', error);
    }
  }

  // Verificar si las notificaciones están habilitadas
  async isSubscribed(): Promise<boolean> {
    if (!this.registration) {
      await this.init();
    }

    if (!this.registration) {
      return false;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      return subscription !== null && Notification.permission === 'granted';
    } catch (error) {
      console.error('Error verificando suscripción:', error);
      return false;
    }
  }

  // Verificar soporte de notificaciones
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  // Obtener estado del permiso
  getPermissionState(): NotificationPermission {
    return Notification.permission;
  }

  // Convertir VAPID key de base64 a Uint8Array
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  // VAPID key por defecto (deberías generar uno propio)
  private getDefaultVapidKey(): string {
    // Esta es una key de ejemplo. En producción, genera tu propia VAPID key
    // con: npx web-push generate-vapid-keys
    return 'BEl62iUYgUivxIkv69yViEuiBIa-Ib27SDbQW5dsxEJw1-2K3-7LHp2L5hq_2bXBgRGRz1m4X9SJNJCJp4Zq6mU';
  }

  // Enviar notificación local (sin servidor)
  async sendLocalNotification(title: string, options?: NotificationOptions): Promise<void> {
    if (!this.registration) {
      await this.init();
    }

    if (!this.registration) {
      throw new Error('Service Worker no disponible');
    }

    if (Notification.permission !== 'granted') {
      throw new Error('Permiso de notificaciones no concedido');
    }

    await this.registration.showNotification(title, {
      icon: '/logo-ceroideas.png',
      badge: '/logo-ceroideas.png',
      ...options
    });
  }
}

export const webPushService = new WebPushService();
